import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ChevronRight } from 'lucide-react';
import { getExecutionStep } from 'features/agents/api/agentsApi';
import { iconFor } from 'features/agents/WorkflowEditor/nodeAppearance';
import { NODE_CATALOG } from 'features/agents/WorkflowEditor/nodeCatalog';
import { RunStatusDot, isRunActive } from 'features/agents/lib/status';
import { formatExecDuration } from 'features/agents/lib/format';
import BrandLoader from 'shared/components/ui/BrandLoader';
import JsonPane from 'features/agents/runs/JsonPane';
import DecideResult from 'features/agents/runs/DecideResult';

/** The message of a step's error, whatever shape the executor stored. */
export function errorText(error) {
  if (!error) return '';
  if (typeof error === 'string') return error;
  if (error.message) return String(error.message);
  try { return JSON.stringify(error); } catch (_) { return String(error); }
}

/**
 * Steps in the order they ran, then the nodes that never ran (a branch not
 * taken) once the run is over. Payloads are not in the list: a step's
 * input, output and logs are fetched when it is opened, and kept.
 */
export default function StepTimeline({ executionId, steps = [], definition, runStatus, openNodeId = null, fetchStep = getExecutionStep }) {
  const [openId, setOpenId] = useState(null);
  const [loaded, setLoaded] = useState({});
  const cache = useRef({});

  const ordered = useMemo(
    () => [...steps].sort((a, b) => (a.step_order ?? 0) - (b.step_order ?? 0)),
    [steps],
  );
  const skipped = useMemo(() => {
    if (isRunActive(runStatus)) return [];
    const ran = new Set(steps.map((s) => s.node_id));
    return (definition?.nodes || []).filter((n) => !ran.has(n.id));
  }, [steps, definition, runStatus]);

  const ensure = useCallback((step) => {
    // A step that is still going changes; only a finished one is kept.
    const key = `${step.id}:${step.status}`;
    if (cache.current[key]) { setLoaded((m) => ({ ...m, [step.id]: cache.current[key] })); return; }
    setLoaded((m) => ({ ...m, [step.id]: { state: 'loading' } }));
    fetchStep(executionId, step.id).then((full) => {
      const val = { state: 'ready', step: full || {} };
      if (!isRunActive(step.status)) cache.current[key] = val;
      setLoaded((m) => ({ ...m, [step.id]: val }));
    }).catch((e) => {
      setLoaded((m) => ({ ...m, [step.id]: { state: 'error', message: e?.response?.data?.error || e?.message || 'Could not load this step' } }));
    });
  }, [executionId, fetchStep]);

  const toggle = (step) => {
    if (openId === step.id) { setOpenId(null); return; }
    setOpenId(step.id);
    ensure(step);
  };

  // A node clicked on the graph opens its step here.
  useEffect(() => {
    if (!openNodeId) return;
    const target = ordered.find((s) => s.node_id === openNodeId);
    if (target) { setOpenId(target.id); ensure(target); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openNodeId]);

  if (!ordered.length && !skipped.length) {
    return <p className="py-6 text-[12px] text-[color:var(--text-muted)]">{isRunActive(runStatus) ? 'Waiting for the first step.' : 'No steps ran.'}</p>;
  }

  return (
    <ol className="m-0 list-none p-0">
      {ordered.map((s) => {
        const Icon = iconFor(s.node_type);
        const open = openId === s.id;
        const err = s.status === 'failed' ? errorText(s.error) : '';
        const data = loaded[s.id];
        const full = data?.step || {};
        return (
          <li key={s.id} className="border-b border-[color:var(--border-subtle)] last:border-b-0">
            <button type="button" onClick={() => toggle(s)} aria-expanded={open}
              className="flex w-full items-start gap-2 px-1 py-2 text-left outline-none transition-colors hover:bg-surface-hover focus-visible:ring-2 focus-visible:ring-[color:var(--state-focus-ring)]">
              <ChevronRight size={13} aria-hidden className={`mt-[3px] shrink-0 text-[color:var(--text-muted)] transition-transform ${open ? 'rotate-90' : ''}`} />
              <span className="mt-[5px]"><RunStatusDot status={s.status} /></span>
              <Icon size={14} aria-hidden className="mt-[2px] shrink-0 text-[color:var(--text-secondary)]" />
              <span className="min-w-0 flex-1">
                <span className="flex items-baseline gap-2">
                  <span className="truncate text-[13px] font-medium text-[color:var(--text-primary)]">{s.node_name || NODE_CATALOG[s.node_type]?.label || s.node_type}</span>
                  <span className="shrink-0 text-[11px] text-[color:var(--text-muted)]">{NODE_CATALOG[s.node_type]?.label || ''}</span>
                  {s.attempt > 1 && <span className="shrink-0 rounded border border-[color:var(--border-subtle)] px-1 text-[10.5px] text-[color:var(--text-muted)]">attempt {s.attempt}</span>}
                </span>
                {err && !open && <span className="mt-0.5 block truncate text-[12px] text-danger">{err}</span>}
              </span>
              <span className="shrink-0 text-[12px] tabular-nums text-[color:var(--text-muted)]">{formatExecDuration(s.duration_ms)}</span>
            </button>
            {open && (
              <div className="pb-3 pl-9 pr-1" data-testid={`step-body-${s.id}`}>
                {err && <p className="mb-2 whitespace-pre-wrap break-words text-[12px] text-danger" role="alert">{err}</p>}
                {(!data || data.state === 'loading') && <div className="flex justify-center py-4"><BrandLoader size="sm" label="Loading step" /></div>}
                {data?.state === 'error' && <p className="text-[12px] text-danger">{data.message}</p>}
                {data?.state === 'ready' && (
                  <div className="flex flex-col gap-3">
                    {s.node_type === 'decide' && full.output && full.output.answers ? (
                      <DecideResult
                        questions={(definition?.nodes || []).find((n) => n.id === s.node_id)?.config?.questions}
                        route={full.output.route}
                        because={full.output.because}
                        answers={full.output.answers}
                      />
                    ) : null}
                    <div className="grid gap-3 sm:grid-cols-2">
                      <JsonPane label="Input" value={full.input} />
                      <JsonPane label="Output" value={full.output} />
                    </div>
                    {full.error && !err && <JsonPane label="Error" value={full.error} tone="danger" />}
                    {full.logs ? <JsonPane label="Logs" value={full.logs} /> : null}
                  </div>
                )}
              </div>
            )}
          </li>
        );
      })}
      {skipped.map((n) => {
        const Icon = iconFor(n.kind);
        return (
          <li key={`skip-${n.id}`} className="flex items-center gap-2 border-b border-[color:var(--border-subtle)] px-1 py-2 text-[color:var(--text-disabled)] last:border-b-0" data-testid="step-skipped">
            <span className="w-[13px] shrink-0" />
            <RunStatusDot status="skipped" />
            <Icon size={14} aria-hidden className="shrink-0" />
            <span className="min-w-0 flex-1 truncate text-[13px]">{n.name}</span>
            <span className="shrink-0 text-[11px]">Skipped</span>
          </li>
        );
      })}
    </ol>
  );
}
